"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useDismissable } from "@/lib/hooks/useDismissable";
// The margin, not the placement engine — see `above` below for why this is
// deliberately not built on `useAnchoredPosition`.
import { VIEWPORT_MARGIN } from "@/lib/anchoredPosition";
import { cn } from "./cn";

/**
 * THE ⋮ OVERFLOW MENU — one component, because there were five (OSO-614).
 *
 * `PostActionMenu`, `CommentItem`, `ConversationMenu`, `GroupHeader` and
 * `SiteHeader` each grew their own, and they had already drifted: the panel was
 * word-for-word identical in the first two apart from `top-7`/`top-8` and
 * `min-w-[120px]`/`min-w-[140px]`, while the rows used three different paddings
 * (`px-4 py-2`, `px-3 py-1.5`, `px-4 py-2.5`) and two different colours for the
 * same thing. Two separate pieces of work asked for this on the same day, from
 * opposite ends of the codebase — the DM bubble's icon row and the document
 * row's five buttons — and both stopped rather than write a sixth copy.
 *
 * WHAT THIS IS NOT. It positions with `absolute` against its own trigger, which
 * is what the menus it replaces did. It does NOT flip when it would run off the
 * bottom of the screen. `ConversationMenu` needs that, measures for it, and
 * carries a height estimate to guess the side before the panel has rendered; it
 * stays on `useAnchoredPosition` (`lib/anchoredPosition.ts`) and is deliberately
 * NOT built on this. Folding it in would mean every ⋮ on the platform carried
 * anchored positioning so that one caller could have it.
 */

/**
 * WHICH LAYOUT GETS THE ⋮, as two class strings — because the answer is the
 * same wherever a hover row and this menu are the same actions twice.
 *
 * A row of icons revealed by hover costs nothing on a mouse and everything on
 * a phone: there is no hover to wait for, so `pointer-fine:` leaves the whole
 * row permanently on screen, taking width from the content it belongs to.
 * OSO-660 photographed that on a DM bubble and OSO-688 on a document row —
 * where five buttons squeezed the file's name to nothing and the line under it
 * rendered on top of them.
 *
 * So: below `lg`, or on a coarse pointer, the row is NOT DRAWN and the same
 * actions are handed to `ActionMenu`. Above it, on a mouse, nothing changes —
 * the icons are quicker than a menu and they were never the complaint.
 *
 * `contents` rather than `flex`, so the buttons stay direct children of the
 * cluster and keep its gap and alignment. They also keep their own
 * `pointer-fine:lg:opacity-*` rules: this wrapper decides whether the row
 * EXISTS, those decide when it fades in once it does (OSO-541).
 *
 * Anything the menu and the row BOTH need — a hidden file input, a dialog —
 * belongs outside both, or the compact layout is asked to click something
 * inside a `hidden` subtree.
 */
export const HOVER_ROW_ONLY = "hidden pointer-fine:lg:contents";
export const COMPACT_ONLY = "pointer-fine:lg:hidden";

const SIZES = {
  /** The comment bubble's menu: tighter rows, smaller trigger. */
  sm: {
    panel: "top-7 min-w-[120px]",
    item: "px-3 py-1.5",
    icon: 14,
    /** No negative margin: the comment's trigger sits in a shrink-to-fit box. */
    trigger: "p-2",
  },
  /** The post card's menu, and the default. */
  md: {
    panel: "top-8 min-w-[140px]",
    item: "px-4 py-2",
    icon: 16,
    /**
     * `-m-1` pulls the 8px padding back out of the row's layout, which is what
     * the post card's trigger has always done. The comment's has not, and
     * hard-coding it here moved that ⋮ 4px up and right — in a change that was
     * supposed to render identically.
     */
    trigger: "p-2 -m-1",
  },
} as const;

/**
 * Lets an item close the menu it is in without every caller threading a
 * setter down. `null` outside a menu, which is what turns a stray
 * `ActionMenuItem` into a loud render-time throw rather than a silent no-op.
 * It is NOT a compile-time error: both are exported from the `ui` barrel and
 * nothing stops the import.
 */
const ActionMenuContext = createContext<{
  /**
   * Closes AND puts focus back on the trigger. A row that simply unmounted
   * itself dropped the caret on `document.body`, so the next Tab restarted
   * from the top of the document — the very failure this component's Tab
   * handling argues against. Worse on a comment, where the trigger is only
   * revealed by `focus-within` and fades out again with nothing to show for
   * it.
   */
  dismiss: () => void;
  size: keyof typeof SIZES;
} | null>(null);

export interface ActionMenuProps {
  /**
   * The trigger's accessible name — "Handlinger for dette opslag", not
   * "Menu". Required: three dots with no name is the single most common
   * screen-reader complaint about an overflow menu, and OSO-589 is an
   * outstanding accessibility pass that will look here.
   */
  label: string;
  size?: keyof typeof SIZES;
  /** Panel edge that lines up with the trigger. Defaults to the right. */
  align?: "left" | "right";
  /** Extra classes for the panel — a caller with a wider trigger than a ⋮. */
  panelClassName?: string;
  /**
   * CONTROLLED, optionally. Omit both and the menu owns its own open state,
   * which is what most callers want. `PostCard` passes them because it also
   * closes the menu from outside — starting an edit, for one.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Classes for the positioning wrapper, e.g. `ml-auto` to push it right. */
  className?: string;
  /**
   * WHAT THE TRIGGER LOOKS LIKE, for the one caller whose trigger is not a ⋮.
   *
   * `GroupHeader`'s membership badge is a pill — a check, "Medlem", a chevron
   * — that doubles as the way out of the group. It had hand-rolled the panel
   * beside it and declared `role="menu"` / `role="menuitem"` on it with none
   * of the keyboard model behind them: the exact promise this component
   * refused to make until it could keep it (OSO-641).
   *
   * The BUTTON is still ours, with its id, its ref and all four ARIA
   * attributes on it — only the contents and the styling come from outside, so
   * a second caller cannot half-implement the contract by accident. Omit both
   * and you get the ⋮.
   */
  triggerContent?: ReactNode;
  triggerClassName?: string;
  /** The rows. `ActionMenuItem`s, and conditional `false`/`null` between them. */
  children: ReactNode;
  /**
   * Rendered inside the dismissable container but OUTSIDE the panel — a second
   * popover in the same corner that outlives the menu.
   * `PostActionMenu`'s flag-reason form is the only user: choosing "Anmeld"
   * closes the panel and opens the form in its place.
   *
   * IT IS NOT DISMISSED BY AN OUTSIDE CLICK, and that is inherited rather than
   * introduced: `useDismissable` only listens while `open` is true, and by the
   * time the form is up the menu is closed. Its own "Annuller" is the way out.
   * Worth fixing, but as its own change with its own test.
   */
  extra?: ReactNode;
}

export function ActionMenu({
  label,
  size = "md",
  align = "right",
  panelClassName,
  open: openProp,
  onOpenChange,
  className,
  triggerContent,
  triggerClassName,
  children,
  extra,
}: ActionMenuProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internalOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (!controlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );

  const close = useCallback(() => setOpen(false), [setOpen]);

  /**
   * `escape: false` is inherited from both menus this replaces, not chosen
   * here. They sit inside surfaces that answer Escape themselves — the
   * lightbox, the dialog — and `useEscapeLayer` is what arbitrates that stack.
   * Making a ⋮ answer Escape is a real accessibility improvement and a real
   * behaviour change; it belongs with that hook and its layer rules, not
   * smuggled in under a refactor that is supposed to render identically.
   */
  /**
   * STILL `escape: false`, and now for a REASON rather than by inheritance.
   *
   * OSO-641 asked for the opt-out to be removed. It is kept, because
   * `useDismissable`'s Escape closes and stops there — it cannot put focus
   * back on the trigger, and a keyboard reader who dismisses a menu and lands
   * at the top of the document has been helped in one hand and robbed with the
   * other. `handleKeyDown` below answers Escape instead, and does both halves.
   *
   * It still marks the key spent, so a single Escape cannot also close the
   * lightbox or dialog this ⋮ is drawn inside. React's handler runs in the
   * bubble phase at the React root, which is BELOW `document` — so it sets
   * `defaultPrevented` before the document-level listeners in
   * `useModalSurface` and `ImageLightbox` ever see the key, and both check it.
   */
  const ref = useDismissable<HTMLDivElement>(open, close, { escape: false });

  const s = SIZES[size];
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const triggerId = useId();

  /**
   * DRAWN ABOVE THE TRIGGER, when below would put it off the bottom edge.
   *
   * A MENU YOU CANNOT REACH IS A MENU THAT IS NOT THERE. The panel is
   * `absolute top-8`, and on the LAST row of anything — the bottom file on a
   * group's document shelf, the last post in a feed — that is below the fold.
   * Absolutely positioned content does not lengthen the page, so there is
   * nothing left to scroll to either: measured on stage after OSO-688 moved
   * five buttons in here, the last file's panel hung 98px under an 844px
   * viewport with the page ALREADY at its end, and "Flyt til mappe" and "Slet"
   * could not be reached at all. Scrolling cannot fix that — there is nowhere
   * to scroll — so the panel is what moves.
   *
   * THIS IS NOT `useAnchoredPosition`, and the distinction this component's
   * header draws still holds: that hook makes a `position: fixed` panel out of
   * two measured rects and a height estimate, for callers that need to be
   * placed anywhere on screen. This is one boolean and one class — the panel
   * stays `absolute`, anchored to its own trigger, and only picks a side.
   */
  const [above, setAbove] = useState(false);

  /**
   * MEASURED AFTER THE PANEL MOUNTS AND BEFORE IT PAINTS, so it never appears
   * below and jumps.
   *
   * FROM THE TRIGGER AND THE PANEL'S HEIGHT, never from the panel's current
   * top and bottom. The height is the same whichever side it hangs on, so this
   * gives the same answer when it re-runs on an already-flipped panel — read
   * off the panel's own box it would find the flipped panel fits, flip it back,
   * and oscillate. That is also why nothing has to be reset when the menu
   * closes.
   *
   * BOTH HALVES MATTER. Hanging it the other way is only an improvement if
   * there is room the other way: on a viewport too short for the panel on
   * either side, flipping trades the bottom rows for the top ones and the
   * reader is no better off.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const trigger = triggerRef.current;
    if (!panel || !trigger) return;
    const height = panel.getBoundingClientRect().height + VIEWPORT_MARGIN;
    const t = trigger.getBoundingClientRect();
    // A measurement is only meaningful after layout, so it genuinely belongs
    // in an effect — the same reason `useAnchoredPosition` states.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAbove(height > window.innerHeight - t.bottom && height <= t.top);
  }, [open]);

  /** The rows a keyboard can land on, in the order they are drawn. */
  const items = useCallback(
    () =>
      Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          '[role="menuitem"]:not([disabled]):not([aria-disabled="true"])',
        ) ?? [],
      ),
    [],
  );

  /**
   * WHICH END THE NEXT OPENING LANDS ON. ArrowUp on a closed trigger means
   * "the last row" — that is what makes it worth pressing on a menu of five —
   * but the panel is not mounted when the key is handled, so the intent is
   * recorded and the effect below spends it. A ref rather than state: nothing
   * renders differently because of it, and it must be readable in the same
   * commit that mounts the panel.
   */
  const landOnRef = useRef<"first" | "last">("first");

  /**
   * FOCUS GOES IN WHEN THE MENU OPENS. Before this, Enter on the trigger set
   * `aria-expanded="true"` and left focus exactly where it was; ArrowDown and
   * ArrowUp did nothing at all. The menu could still be operated, because the
   * rows were real buttons in DOM order and Tab reached them — just not the
   * way a thing calling itself a menu says it can be.
   */
  useEffect(() => {
    if (!open) {
      landOnRef.current = "first";
      return;
    }
    const rows = items();
    // `preventScroll`, because this runs for a POINTER open too: the panel is
    // already at the pointer, but focusing a row without it scrolls the
    // nearest scrollable ancestor and the post under the cursor jumps.
    (landOnRef.current === "last" ? rows[rows.length - 1] : rows[0])?.focus({
      preventScroll: true,
    });
  }, [open, items]);

  function moveFocus(delta: number, absolute?: "first" | "last") {
    const rows = items();
    if (rows.length === 0) return;
    if (absolute) {
      (absolute === "first" ? rows[0] : rows[rows.length - 1]).focus({ preventScroll: true });
      return;
    }
    const at = rows.indexOf(document.activeElement as HTMLElement);
    // Wraps, and an unknown position (focus on the trigger) enters from the
    // end Arrow was heading for.
    const next = at === -1 ? (delta > 0 ? 0 : rows.length - 1) : (at + delta + rows.length) % rows.length;
    rows[next].focus({ preventScroll: true });
  }

  function dismiss() {
    close();
    triggerRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.defaultPrevented) return;

    if (e.key === "Escape") {
      if (!open) return;
      e.preventDefault();
      dismiss();
      return;
    }

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      /**
       * ONLY FROM THE TRIGGER OR A ROW. `extra` renders inside this same
       * wrapper — `PostActionMenu`'s flag-reason `<textarea>`, which appears
       * exactly when the panel has closed — so an unguarded branch here ate
       * ArrowUp while somebody was fixing a typo in their report, and reopened
       * the menu on top of the form to do it.
       */
      const from = e.target as HTMLElement;
      if (from !== triggerRef.current && !from.closest?.('[role="menuitem"]')) return;
      e.preventDefault();
      if (!open) {
        // Opening from the trigger enters the list at the end the key points
        // at, which is what makes ArrowUp useful on a menu of five rows.
        landOnRef.current = e.key === "ArrowUp" ? "last" : "first";
        setOpen(true);
        return;
      }
      moveFocus(e.key === "ArrowDown" ? 1 : -1);
      return;
    }

    if (!open) return;

    if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      moveFocus(0, e.key === "Home" ? "first" : "last");
      return;
    }

    /**
     * TAB CLOSES THE MENU AND SPENDS ITSELF DOING IT. The APG says Tab should
     * close the menu and continue to the next control, but the row holding
     * focus is about to unmount — so "continue" would strand the caret on
     * `document.body`. Consuming the keystroke and handing focus back to the
     * trigger costs one extra Tab and never loses focus, which is the better
     * of the two failures.
     */
    if (e.key === "Tab") {
      e.preventDefault();
      dismiss();
    }
  }

  return (
    <div className={cn("relative", className)} ref={ref} onKeyDown={handleKeyDown}>
      <button
        type="button"
        ref={triggerRef}
        id={triggerId}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen(!open)}
        className={cn(
          triggerContent
            ? triggerClassName
            : cn(
                "rounded-full text-subtle-foreground hover:text-muted-foreground hover:bg-muted transition-colors",
                s.trigger,
              ),
        )}
      >
        {triggerContent ?? (
        <svg
          width={s.icon}
          height={s.icon}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
        )}
      </button>

      {/*
        `role="menu"` IS NOW EARNED. OSO-614 deliberately did not declare it,
        and was right not to: the role promises roving focus, arrow keys and
        Home/End, a screen reader switches to application mode to honour that
        promise, and declaring it without implementing it is worse than staying
        silent — the promised keys do nothing and Tab stops behaving the way
        the reader was told it would. That comment named OSO-589 as where the
        model would be decided. This is it (OSO-641).
      */}
      {open && (
        <div
          data-testid="action-menu-panel"
          ref={panelRef}
          id={panelId}
          role="menu"
          aria-labelledby={triggerId}
          className={cn(
            "absolute bg-surface rounded-lg shadow-lg border border-border py-1 z-50",
            align === "right" ? "right-0" : "left-0",
            s.panel,
            // `top-auto` is what lets `tailwind-merge` drop `s.panel`'s own
            // `top-7`/`top-8`; `cn` resolves by call order, so this wins.
            above && "top-auto bottom-full mb-1",
            panelClassName,
          )}
        >
          <ActionMenuContext.Provider value={{ dismiss, size }}>
            {children}
          </ActionMenuContext.Provider>
        </div>
      )}

      {extra}
    </div>
  );
}

/**
 * A row that states something instead of doing it — "Anmeldt ✓", where the
 * action has already been taken and the row is now its own receipt. It exists
 * so that row does not hand-write the size's padding a second time, which is
 * exactly how the four paddings this component replaces came about.
 */
export function ActionMenuNote({ children }: { children: ReactNode }) {
  const ctx = useContext(ActionMenuContext);
  if (!ctx) {
    throw new Error("ActionMenuNote must be rendered inside an ActionMenu");
  }
  return (
    <span
      /**
       * A DISABLED `menuitem`, not `role="none"`.
       *
       * Both keep it unselectable, and only one keeps it AUDIBLE. Now that the
       * panel is a real `menu`, a screen reader walks it in application mode
       * and visits `menuitem`s only — so `role="none"` would silently drop
       * "Anmeldt ✓", which is the receipt telling somebody their report is
       * already filed. `aria-disabled` also keeps it out of `items()`, whose
       * selector excludes it, so the arrow keys still skip it.
       */
      role="menuitem"
      aria-disabled="true"
      className={cn(
        "block text-sm text-subtle-foreground",
        SIZES[ctx.size].item,
      )}
    >
      {children}
    </span>
  );
}

export interface ActionMenuItemProps {
  onSelect: () => void;
  /** Red text and a red hover — deleting, leaving, blocking. */
  destructive?: boolean;
  disabled?: boolean;
  /**
   * Keep the menu open after this row is chosen.
   *
   * One user: the post card's "Link til opslag". Its handler closes the menu
   * only when the copy SUCCEEDED, because on failure the row itself becomes
   * "Kunne ikke kopiere" — auto-closing would take the message away before it
   * could be read.
   *
   * NOT for "Anmeld", which opens the flag form: that form is drawn at the
   * same `right-0 top-8 z-50` as the panel, so keeping both open stacks them.
   */
  keepOpen?: boolean;
  children: ReactNode;
}

export function ActionMenuItem({
  onSelect,
  destructive,
  disabled,
  keepOpen,
  children,
}: ActionMenuItemProps) {
  const ctx = useContext(ActionMenuContext);
  if (!ctx) {
    throw new Error("ActionMenuItem must be rendered inside an ActionMenu");
  }
  const { dismiss, size } = ctx;

  return (
    <button
      type="button"
      role="menuitem"
      /**
       * ROVING, so the panel is one stop rather than five. The arrow keys in
       * `ActionMenu` move focus between these; Tab does not walk them, which
       * is the half of the contract `role="menu"` promises and OSO-614
       * refused to promise without.
       */
      tabIndex={-1}
      disabled={disabled}
      onClick={() => {
        // `onSelect` first: it may open a dialog, and that dialog records
        // whatever holds focus when it mounts as the place to return to. The
        // trigger is the right answer there; `document.body` is not.
        onSelect();
        if (!keepOpen) dismiss();
      }}
      className={cn(
        "w-full text-left text-sm transition-colors disabled:opacity-50",
        SIZES[size].item,
        destructive
          ? "text-destructive hover:bg-destructive-muted"
          : "text-foreground hover:bg-surface-2",
      )}
    >
      {children}
    </button>
  );
}
